/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ENVIRONMENT } from '../settings/custom.tokens';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { LocalStorageKeys } from '../local-storage/local-storage-keys.class';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { Environment } from '@iot-platform/models/common';
import { ExportType } from '@iot-platform/models/grid-engine';
import { get } from 'lodash';
import { catchError, EMPTY, from, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';

// Declare global gapi and google (if not already done in typings)
declare const gapi: any;
declare const google: any;

// Add the enum here (or import if in a separate file)
export enum UploadStatusMessage {
  NoFileSelected = 'NoFileSelected',
  CheckingAuthAndPreparing = 'CheckingAuthAndPreparing',
  NotAuthenticatedAttemptingSignIn = 'NotAuthenticatedAttemptingSignIn',
  AuthenticationRequired = 'AuthenticationRequired',
  AuthenticationFailed = 'AuthenticationFailed',
  Uploading = 'Uploading',
  UploadSuccessful = 'UploadSuccessful',
  UploadFailed = 'UploadFailed',
  SignedOut = 'SignedOut',
  InvalidUploadType = 'InvalidUploadType',
  LinkNotAvailable = 'LinkNotAvailable',
  UnexpectedError = 'UnexpectedError',
  GoogleClientNotInitialized = 'GoogleClientNotInitialized',
  GoogleLibrariesFailed = 'GoogleLibrariesFailed',
  GoogleClientInitError = 'GoogleClientInitError',
  GoogleGSILibraryNotLoaded = 'GoogleGSILibraryNotLoaded'
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

// Define a type for the file upload response
interface GoogleDriveFileResponse {
  id: string;
  kind: string;
  mimeType: string;
  name: string;
  webViewLink?: string;
  alternateLink?: string; // Keep for now in interface as it's in the current code, but remove from fields param
}

@Injectable({
  providedIn: 'root'
})
export class GoogleDriveService {
  tokenClient: any; // GSI token client instance
  storage: LocalStorageService = inject(LocalStorageService);
  loading: WritableSignal<boolean> = signal(false);
  accessToken = signal<string | null>(null);
  tokenExpiryTime = signal<number>(0);
  isAuthenticated = signal(false);
  isAuthenticated$ = toObservable(this.isAuthenticated);
  uploadStatus: WritableSignal<UploadStatusMessage | null> = signal(null);
  destroy$: Subject<void> = new Subject<void>();
  selectedFile!: File;
  selectedFileName!: string;
  selectedFileType: ExportType = ExportType.XLSX;
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly CLIENT_ID = get(this.environment, 'googleDrive.clientId') as string;
  private readonly API_KEY = get(this.environment, 'googleDrive.apiKey') as string;
  private readonly SCOPES = get(this.environment, 'googleDrive.scopes') as string;
  private readonly DISCOVERY_DOCS = get(this.environment, 'googleDrive.discoveryDocs') as string[];
  private readonly UPLOAD_URI = get(this.environment, 'googleDrive.uploadUri') as string;
  private readonly CLIENT_SCRIPT_URI = get(this.environment, 'googleDrive.clientScriptUri') as string;
  private readonly API_SCRIPT_URI = get(this.environment, 'googleDrive.apiScriptUri') as string;
  private readonly FILE_URI = get(this.environment, 'googleDrive.fileUri') as string;
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  canUploadToGoogleDrive: Signal<boolean> = this.featureFlagsService.canUploadToGoogleDrive;

  constructor() {
    this.accessToken.set(this.storage.get(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN));
    const tokenExpiryTime = this.storage.get(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN_EXPIRY_TIME);
    if (tokenExpiryTime) {
      this.tokenExpiryTime.set(Number(tokenExpiryTime));
    }
    effect(() => {
      const accessToken = this.accessToken();
      if (accessToken) {
        this.storage.set(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN, accessToken);
        this.storage.set(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN_EXPIRY_TIME, `${this.tokenExpiryTime()}`);
      } else {
        this.storage.remove(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN);
        this.storage.remove(LocalStorageKeys.STORAGE_GOOGLE_DRIVE_ACCESS_TOKEN_EXPIRY_TIME);
      }
      this.isAuthenticated.set(!!accessToken);
    });
  }

  /**
   * Initializes the Google API client and GSI token client.
   * Ensures Google libraries are loaded before proceeding.
   */
  loadGoogleLibraries(): void {
    if (this.canUploadToGoogleDrive()) {
      this.loadScript(this.CLIENT_SCRIPT_URI)
        .then(() => {
          this.initializeGsiTokenClient();
          return this.loadScript(this.API_SCRIPT_URI);
        })
        .then(
          () =>
            new Promise<void>((resolve, reject) => {
              gapi.load('client:auth2', () => {
                gapi.client
                  .init({
                    apiKey: this.API_KEY, // Use API_KEY for gapi.client.init
                    clientId: this.CLIENT_ID,
                    discoveryDocs: this.DISCOVERY_DOCS
                  })
                  .then(
                    () => {
                      resolve();
                    },
                    (error: any) => {
                      this.uploadStatus.set(UploadStatusMessage.GoogleClientInitError);
                      reject(error);
                    }
                  );
              });
            })
        )
        .catch(() => {
          this.uploadStatus.set(UploadStatusMessage.GoogleLibrariesFailed);
        });
    }
  }

  /**
   * Initiates the Google Sign-in flow.
   * Returns an Observable that emits true on successful authentication, false on sign out, or an error.
   */
  signIn(): Observable<boolean> {
    if (!this.tokenClient) {
      this.uploadStatus.set(UploadStatusMessage.GoogleClientNotInitialized);
      return new Observable((observer) => observer.error(UploadStatusMessage.GoogleClientNotInitialized));
    }

    this.requestAccessToken();

    return this.isAuthenticated$.pipe(
      filter((authenticated) => authenticated),
      take(1),
      takeUntil(this.destroy$)
    );
  }

  requestAccessToken() {
    this.tokenClient.requestAccessToken({ prompt: '' });
  }

  /**
   * Signs out the user by clearing the access token.
   */
  signOut(): void {
    this.accessToken.set(null);
    this.isAuthenticated.set(false);
    this.uploadStatus.set(UploadStatusMessage.SignedOut);
  }

  /**
   * Uploads an Excel file to Google Drive.
   * @param file The Excel file to upload.
   * @param fileName The desired name for the file in Google Drive.
   * @returns An Observable that emits the Google Drive file response on success.
   */
  uploadExcelFile(file: File, fileName: string): Observable<GoogleDriveFileResponse | null> {
    const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    return this.prepareAndUploadFile(file, fileName, mimeType);
  }

  /**
   * IMPORTANT: This method should typically be called from a component, not directly from the service.
   * It orchestrates the file selection and upload process.
   * @param file The file to upload.
   * @param fileName The desired name for the file in Google Drive.
   * @param type The type of file to upload.
   */
  triggerUpload(file: File, fileName: string, type: ExportType): void {
    let uploadObservable: Observable<GoogleDriveFileResponse | null> = EMPTY;
    this.selectedFile = file;
    this.selectedFileName = fileName;
    this.selectedFileType = type;

    // Determine which file to upload and which service method to call
    if (type === ExportType.XLSX) {
      if (file) {
        uploadObservable = this.uploadExcelFile(file, fileName);
      } else {
        this.uploadStatus.set(UploadStatusMessage.NoFileSelected);
        return;
      }
    } else {
      this.uploadStatus.set(UploadStatusMessage.InvalidUploadType);
      return;
    }

    this.loading.set(true);
    // Subscribe to the upload observable and handle the response
    uploadObservable
      .pipe(
        catchError(() => {
          this.uploadStatus.set(UploadStatusMessage.UploadFailed);
          return of(null);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          if (response && response.id) {
            this.uploadStatus.set(UploadStatusMessage.UploadSuccessful);
            const webViewLink = response.webViewLink;
            if (webViewLink) {
              window.open(webViewLink, '_blank');
            } else {
              this.uploadStatus.set(UploadStatusMessage.LinkNotAvailable);
              const fileId = response.id;
              if (fileId) {
                const googleDriveFileLink = this.FILE_URI.replace('{{id}}', fileId);
                window.open(googleDriveFileLink, '_blank');
              } else {
                this.uploadStatus.set(UploadStatusMessage.LinkNotAvailable);
              }
            }
          }
        },
        error: () => {
          this.uploadStatus.set(UploadStatusMessage.UnexpectedError);
          this.loading.set(false);
        },
        complete: () => {
          this.loading.set(false);
          this.clearStream();
        }
      });
  }

  /**
   * Initializes the Google Sign-in (GSI) token client.
   */
  initializeGsiTokenClient(): void {
    if (typeof google === 'undefined' || typeof google.accounts === 'undefined' || typeof google.accounts.oauth2 === 'undefined') {
      this.uploadStatus.set(UploadStatusMessage.GoogleGSILibraryNotLoaded);
      return;
    }

    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: this.CLIENT_ID,
      scope: this.SCOPES,
      callback: (tokenResponse: any) => this.handleTokenResponse(tokenResponse),
      error_callback: () => this.handleErrorResponse()
    });
  }

  clearStream() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$.unsubscribe();
    this.destroy$ = new Subject<void>();
  }

  /**
   * Handles the successful token response from Google.
   * Updates internal state and RxJS subjects.
   * @param tokenResponse The token response object.
   */
  private handleTokenResponse(tokenResponse: TokenResponse): void {
    const newAccessToken = tokenResponse?.access_token || null;
    this.accessToken.set(newAccessToken);
    this.tokenExpiryTime.set(newAccessToken ? Date.now() + tokenResponse.expires_in * 1000 : 0); // Convert seconds to milliseconds
    this.isAuthenticated.set(!!newAccessToken);
    this.triggerUpload(this.selectedFile, this.selectedFileName, this.selectedFileType);
  }

  /**
   * Handles error responses during the token request.
   */
  private handleErrorResponse(): void {
    this.accessToken.set(null);
    this.tokenExpiryTime.set(0);
    this.isAuthenticated.set(false);
    this.uploadStatus.set(UploadStatusMessage.AuthenticationFailed);
    this.loading.set(false);
    this.clearStream();
  }

  /**
   * Helper to load external scripts dynamically.
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }

  /**
   * Internal function to prepare the file and handle authentication before calling executeUpload.
   * This centralizes the pre-upload logic.
   */
  private prepareAndUploadFile(file: File, fileName: string, mimeType: string): Observable<GoogleDriveFileResponse | null> {
    this.uploadStatus.set(UploadStatusMessage.CheckingAuthAndPreparing);

    // Check if the user is authenticated and if the token is valid
    const isTokenValid = this.tokenExpiryTime() && Date.now() < this.tokenExpiryTime() - 10000; // 10 seconds before expiry
    if (!this.isAuthenticated() || !isTokenValid) {
      this.uploadStatus.set(UploadStatusMessage.NotAuthenticatedAttemptingSignIn);
      return this.signIn().pipe(
        switchMap((signInSuccess) => {
          if (signInSuccess) {
            return this.executeUpload(file, fileName, mimeType);
          } else {
            this.uploadStatus.set(UploadStatusMessage.AuthenticationRequired);
            return of(null);
          }
        }),
        catchError(() => {
          this.uploadStatus.set(UploadStatusMessage.AuthenticationFailed);
          return of(null);
        }),
        takeUntil(this.destroy$)
      );
    } else {
      // Already authenticated, directly upload the file
      return this.executeUpload(file, fileName, mimeType);
    }
  }

  /**
   * Internal function to execute the file upload to Google Drive using the native Fetch API.
   * @param file The file to upload.
   * @param fileName The desired name for the file.
   * @param mimeType The MIME type of the file.
   * @returns An Observable that emits the Google Drive API response.
   */
  private executeUpload(file: File, fileName: string, mimeType: string): Observable<GoogleDriveFileResponse> {
    this.uploadStatus.set(UploadStatusMessage.Uploading);
    const metadata = {
      name: fileName,
      mimeType
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    return from(
      fetch(this.UPLOAD_URI, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.accessToken()}` },
        body: form
      }).then((res) => {
        // Handle non-ok responses from fetch
        if (!res.ok) {
          return res.json().then((errorData) => {
            if (res.status === 401) {
              // In case the token is expired or invalid, request a new token
              this.requestAccessToken();
            }
            throw new Error(`Google Drive API error (${res.status}): ${JSON.stringify(errorData)}`);
          });
        }
        return res.json() as Promise<GoogleDriveFileResponse>;
      })
    ).pipe(
      catchError((error: any) => {
        this.uploadStatus.set(UploadStatusMessage.UploadFailed);
        return new Observable<GoogleDriveFileResponse>((observer) => observer.error(error));
      }),
      takeUntil(this.destroy$)
    ) as Observable<GoogleDriveFileResponse>;
  }
}
